import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { getCanadianTime } from '@/hooks/useCanadianTime';

// Types for the admin booking data
export interface AdminPassengerData {
  adult: number;
  senior: number;
  child: number;
  special_assistance: boolean;
  special_assistance_reason?: string;
}

export interface AdminVehicleData {
  height: string;
  length: string;
  vehicle_return: boolean;
  type?: string;
}

export interface AdminRouteData {
  id: string;
  departure: string;
  destination: string;
  status: boolean;
  routeGroupId: string;
}

export interface AdminPaymentData {
  method: number | null; // 1 for Credit Card, 2 for Cash
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  cardNumber?: string;
  expiry?: string;
  cvv?: string;
  cardholderName?: string;
}

export interface AdminBookingData {
  routeId: string;
  passenger: AdminPassengerData;
  vehicle: AdminVehicleData;
  payment: AdminPaymentData;
  departure: string; // YYYY-MM-DD format
  returning?: string; // YYYY-MM-DD format
  round: 'one' | 'two';
}

export interface AdminBookingState {
  // Form data
  bookingData: AdminBookingData;

  // UI display values
  vehicleCount: number; // 0 for walk-on, 1+ for vehicles

  // Available routes
  routes: AdminRouteData[];
  locations: string[];

  // UI state
  tripType: 'roundtrip' | 'oneway';
  currentStep: number; // Track which step of admin booking we're on

  // Currently selected dates (UI state)
  currentSelectedDates: {
    departure: string; // YYYY-MM-DD format
    returning?: string; // YYYY-MM-DD format
  };

  // API state
  isLoading: boolean;
  searchResults: any[];
  error: string | null;

  // Admin specific states
  isInitialized: boolean;
}

// Get current date and tomorrow's date in YYYY-MM-DD format using Canadian time - Fixed: Pure string manipulation
const getCurrentDatesForAdmin = () => {
  const canadianTime = getCanadianTime();

  // Format today
  const todayYear = canadianTime.year;
  const todayMonth = (canadianTime.monthIndex + 1).toString().padStart(2, '0');
  const todayDay = canadianTime.day.toString().padStart(2, '0');
  const todayString = `${todayYear}-${todayMonth}-${todayDay}`;

  // Calculate tomorrow by adding 1 day using string manipulation
  let tomorrowDay = canadianTime.day + 1;
  let tomorrowMonth = canadianTime.monthIndex + 1;
  let tomorrowYear = canadianTime.year;

  // Handle month rollover - simplified check for common months
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  // Check for leap year
  if (tomorrowYear % 4 === 0 && (tomorrowYear % 100 !== 0 || tomorrowYear % 400 === 0)) {
    daysInMonth[1] = 29;
  }

  if (tomorrowDay > daysInMonth[canadianTime.monthIndex]) {
    tomorrowDay = 1;
    tomorrowMonth++;
    if (tomorrowMonth > 12) {
      tomorrowMonth = 1;
      tomorrowYear++;
    }
  }

  const tomorrowString = `${tomorrowYear}-${tomorrowMonth.toString().padStart(2, '0')}-${tomorrowDay.toString().padStart(2, '0')}`;

  return {
    departure: todayString,
    returning: tomorrowString
  };
};

const currentDates = getCurrentDatesForAdmin();

const initialState: AdminBookingState = {
  bookingData: {
    routeId: '',
    passenger: {
      adult: 0, // Start with 0 - admin must explicitly select passengers
      senior: 0,
      child: 0,
      special_assistance: false,
      special_assistance_reason: ''
    },
    vehicle: {
      height: 'standard',
      length: 'standard',
      vehicle_return: true,
      type: 'Car' // Default to car for admin booking
    },
    payment: {
      method: null,
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: ''
    },
    departure: currentDates.departure,
    returning: currentDates.returning,
    round: 'two' // Default to round trip
  },
  vehicleCount: 1, // Start with 1 vehicle for admin
  routes: [],
  locations: [],
  tripType: 'roundtrip',
  currentStep: 1,
  currentSelectedDates: {
    departure: currentDates.departure,
    returning: currentDates.returning
  },
  isLoading: false,
  searchResults: [],
  error: null,
  isInitialized: false
};

// Async thunk for fetching routes (admin version)
export const fetchAdminRoutes = createAsyncThunk(
  'adminBooking/fetchRoutes',
  async (_, { rejectWithValue }) => {
    try {
      const { getRoutes } = await import('@/lib/api/trip');
      const response = await getRoutes();

      if (response.success && response.data?.route) {
        return response.data.route;
      } else {
        throw new Error('Failed to fetch routes');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch routes');
    }
  }
);

// Async thunk for searching trips (admin version)
export const searchAdminTrips = createAsyncThunk(
  'adminBooking/searchTrips',
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState() as { adminBooking: AdminBookingState };
      const { bookingData, vehicleCount } = state.adminBooking;

      // Validate that we have at least 1 passenger OR a vehicle
      const totalPassengers = bookingData.passenger.adult + bookingData.passenger.senior + bookingData.passenger.child;
      const hasVehicle = vehicleCount > 0;
      if (totalPassengers === 0 && !hasVehicle) {
        return rejectWithValue('At least one passenger or vehicle is required');
      }

      // Build the API URL with query parameters
      const params = new URLSearchParams();
      params.append('route', bookingData.routeId);
      params.append('passenger', JSON.stringify(bookingData.passenger));

      // Handle vehicle parameter - empty string if no vehicle selected
      if (vehicleCount === 0) {
        params.append('vehicle', '""');
      } else {
        params.append('vehicle', JSON.stringify(bookingData.vehicle));
      }

      params.append('departure', bookingData.departure);
      if (bookingData.returning && bookingData.round === 'two') {
        params.append('returning', bookingData.returning);
      }
      params.append('round', bookingData.round);

      const { default: api } = await import('@/lib/api');
      const response = await api.get(`/trip/search?${params.toString()}`);

      // If successful, dispatch the results to tripResults slice
      if (response.data.success && response.data.data) {
        const { setTripResults } = await import('./tripResultsSlice');
        dispatch(setTripResults(response.data.data));
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to search trips');
    }
  }
);

// Async thunk for searching trips with custom dates (admin version)
export const searchAdminTripsWithCustomDates = createAsyncThunk(
  'adminBooking/searchTripsWithCustomDates',
  async (customDates: { departure?: string; returning?: string }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState() as { adminBooking: AdminBookingState };
      const { bookingData, vehicleCount } = state.adminBooking;

      // Validate that we have at least 1 passenger OR a vehicle
      const totalPassengers = bookingData.passenger.adult + bookingData.passenger.senior + bookingData.passenger.child;
      const hasVehicle = vehicleCount > 0;
      if (totalPassengers === 0 && !hasVehicle) {
        return rejectWithValue('At least one passenger or vehicle is required');
      }

      // Build the API URL with query parameters
      const params = new URLSearchParams();
      params.append('route', bookingData.routeId);
      params.append('passenger', JSON.stringify(bookingData.passenger));

      // Handle vehicle parameter - empty string if no vehicle selected
      if (vehicleCount === 0) {
        params.append('vehicle', '""');
      } else {
        params.append('vehicle', JSON.stringify(bookingData.vehicle));
      }

      // Use custom dates if provided, otherwise use stored dates
      params.append('departure', customDates.departure || bookingData.departure);
      if (bookingData.round === 'two') {
        const returningDate = customDates.returning || bookingData.returning;
        if (returningDate) {
          params.append('returning', returningDate);
        }
      }
      params.append('round', bookingData.round);

      const { default: api } = await import('@/lib/api');
      const response = await api.get(`/trip/search?${params.toString()}`);

      // If successful, dispatch the results to tripResults slice
      if (response.data.success && response.data.data) {
        const { setTripResults } = await import('./tripResultsSlice');
        dispatch(setTripResults(response.data.data));
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to search trips');
    }
  }
);

const adminBookingSlice = createSlice({
  name: 'adminBooking',
  initialState,
  reducers: {
    // Trip type actions
    setAdminTripType: (state, action: PayloadAction<'roundtrip' | 'oneway'>) => {
      state.tripType = action.payload;
      state.bookingData.round = action.payload === 'roundtrip' ? 'two' : 'one';

      // Clear returning date for one-way trips
      if (action.payload === 'oneway') {
        state.bookingData.returning = undefined;
      } else {
        // Set returning date to departure date if not set
        if (!state.bookingData.returning) {
          state.bookingData.returning = state.bookingData.departure;
        }
      }
    },

    // Route actions
    setAdminFromLocation: (state, action: PayloadAction<string>) => {
      const selectedRoute = state.routes.find(route =>
        route.departure === action.payload
      );

      if (selectedRoute) {
        state.bookingData.routeId = selectedRoute.id;
      }
    },

    setAdminToLocation: (state, action: PayloadAction<string>) => {
      const selectedRoute = state.routes.find(route =>
        route.destination === action.payload
      );

      if (selectedRoute) {
        state.bookingData.routeId = selectedRoute.id;
      }
    },

    setAdminRouteByDirection: (state, action: PayloadAction<{ departure: string; destination: string }>) => {
      const selectedRoute = state.routes.find(route =>
        route.departure === action.payload.departure &&
        route.destination === action.payload.destination
      );

      if (selectedRoute) {
        state.bookingData.routeId = selectedRoute.id;
      }
    },

    setAdminRouteById: (state, action: PayloadAction<string>) => {
      state.bookingData.routeId = action.payload;
    },

    // Date actions
    setAdminDepartureDate: (state, action: PayloadAction<string>) => {
      state.bookingData.departure = action.payload;

      // If returning date exists and is before the new departure date, update it to match departure date
      // Fixed: Pure string comparison following TIME_HANDLING_GUIDELINES
      if (state.bookingData.returning) {
        // Compare YYYY-MM-DD strings directly (works due to format)
        if (state.bookingData.returning < action.payload) {
          state.bookingData.returning = action.payload;
        }
      }
    },

    setAdminReturningDate: (state, action: PayloadAction<string>) => {
      state.bookingData.returning = action.payload;
    },

    // Passenger actions
    setAdminPassengerData: (state, action: PayloadAction<Partial<AdminPassengerData>>) => {
      const updatedPassenger = {
        ...state.bookingData.passenger,
        ...action.payload
      };

      // Allow 0 passengers (they can select vehicle instead)
      state.bookingData.passenger = updatedPassenger;
    },

    // Vehicle actions
    setAdminVehicleData: (state, action: PayloadAction<Partial<AdminVehicleData>>) => {
      state.bookingData.vehicle = {
        ...state.bookingData.vehicle,
        ...action.payload
      };
    },

    // Vehicle count actions
    setAdminVehicleCount: (state, action: PayloadAction<number>) => {
      state.vehicleCount = Math.max(0, action.payload); // Ensure non-negative

      // Update vehicle type based on count
      if (state.vehicleCount === 0) {
        state.bookingData.vehicle.type = 'Foot Passenger';
      } else if (!state.bookingData.vehicle.type || state.bookingData.vehicle.type === 'Foot Passenger') {
        state.bookingData.vehicle.type = 'Car'; // Default to car when adding vehicle
      }
    },

    // Payment actions
    setAdminPaymentData: (state, action: PayloadAction<Partial<AdminPaymentData>>) => {
      state.bookingData.payment = {
        ...state.bookingData.payment,
        ...action.payload
      };
    },

    // Step management
    setAdminCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },

    nextAdminStep: (state) => {
      state.currentStep += 1;
    },

    previousAdminStep: (state) => {
      state.currentStep = Math.max(1, state.currentStep - 1);
    },

    // Actions for currently selected dates (UI state)
    setAdminCurrentSelectedDepartureDate: (state, action: PayloadAction<string>) => {
      state.currentSelectedDates.departure = action.payload;
    },

    setAdminCurrentSelectedReturnDate: (state, action: PayloadAction<string>) => {
      state.currentSelectedDates.returning = action.payload;
    },

    setAdminCurrentSelectedDates: (state, action: PayloadAction<{ departure: string; returning?: string }>) => {
      state.currentSelectedDates.departure = action.payload.departure;
      if (action.payload.returning) {
        state.currentSelectedDates.returning = action.payload.returning;
      }
    },

    // Reset search results
    clearAdminSearchResults: (state) => {
      state.searchResults = [];
      state.error = null;
    },

    // Initialize admin booking
    initializeAdminBooking: (state) => {
      state.isInitialized = true;
      // Set default route only if no route is already set
      if (state.routes.length > 0 && !state.bookingData.routeId) {
        const firstRoute = state.routes[0];
        state.bookingData.routeId = firstRoute.id;
      }
    },

    // Reset admin booking to initial state
    resetAdminBooking: (state) => {
      const currentDates = getCurrentDatesForAdmin();

      state.bookingData = {
        routeId: state.routes.length > 0 ? state.routes[0].id : '',
        passenger: {
          adult: 0,
          senior: 0,
          child: 0,
          special_assistance: false,
          special_assistance_reason: ''
        },
        vehicle: {
          height: 'standard',
          length: 'standard',
          vehicle_return: true,
          type: 'Car'
        },
        payment: {
          method: null,
          firstName: '',
          lastName: '',
          email: '',
          phoneNumber: ''
        },
        departure: currentDates.departure,
        returning: currentDates.returning,
        round: 'two'
      };

      state.vehicleCount = 1;
      state.tripType = 'roundtrip';
      state.currentStep = 1;
      state.currentSelectedDates = {
        departure: currentDates.departure,
        returning: currentDates.returning
      };
      state.error = null;
    },

    // Clear admin booking slice completely (for after successful booking)
    clearAdminBookingSlice: (state) => {
      const currentDates = getCurrentDatesForAdmin();

      // Reset to completely fresh initial state
      state.bookingData = {
        routeId: '',
        passenger: {
          adult: 0,
          senior: 0,
          child: 0,
          special_assistance: false,
          special_assistance_reason: ''
        },
        vehicle: {
          height: 'standard',
          length: 'standard',
          vehicle_return: true,
          type: 'Car'
        },
        payment: {
          method: null,
          firstName: '',
          lastName: '',
          email: '',
          phoneNumber: ''
        },
        departure: currentDates.departure,
        returning: currentDates.returning,
        round: 'two'
      };

      state.vehicleCount = 1;
      state.tripType = 'roundtrip';
      state.currentStep = 1;
      state.currentSelectedDates = {
        departure: currentDates.departure,
        returning: currentDates.returning
      };
      state.isLoading = false;
      state.searchResults = [];
      state.error = null;
      state.isInitialized = false;

      // Keep routes as they don't need to be refetched
      // state.routes = []; // Don't clear routes
      // state.locations = []; // Don't clear locations
    }
  },
  extraReducers: (builder) => {
    // Fetch routes
    builder
      .addCase(fetchAdminRoutes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminRoutes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.routes = action.payload;

        // Extract unique locations
        const uniqueLocations = new Set<string>();
        action.payload.forEach((route: AdminRouteData) => {
          uniqueLocations.add(route.departure);
          uniqueLocations.add(route.destination);
        });

        state.locations = Array.from(uniqueLocations);

        // Set default route if available and not already set
        if (action.payload.length > 0 && !state.bookingData.routeId) {
          const firstRoute = action.payload[0];
          state.bookingData.routeId = firstRoute.id;
        }
      })
      .addCase(fetchAdminRoutes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;

        // Fallback locations
        state.locations = ['Aero Link', 'Tuck Inlet'];
      });

    // Search trips
    builder
      .addCase(searchAdminTrips.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchAdminTrips.fulfilled, (state, action) => {
        state.isLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchAdminTrips.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Search trips with custom dates
    builder
      .addCase(searchAdminTripsWithCustomDates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchAdminTripsWithCustomDates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchAdminTripsWithCustomDates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const {
  setAdminTripType,
  setAdminFromLocation,
  setAdminToLocation,
  setAdminRouteByDirection,
  setAdminRouteById,
  setAdminDepartureDate,
  setAdminReturningDate,
  setAdminPassengerData,
  setAdminVehicleData,
  setAdminVehicleCount,
  setAdminPaymentData,
  setAdminCurrentStep,
  nextAdminStep,
  previousAdminStep,
  setAdminCurrentSelectedDepartureDate,
  setAdminCurrentSelectedReturnDate,
  setAdminCurrentSelectedDates,
  clearAdminSearchResults,
  initializeAdminBooking,
  resetAdminBooking,
  clearAdminBookingSlice
} = adminBookingSlice.actions;

export default adminBookingSlice.reducer;